import { MessageSourceProps, withMessages } from "react-message-source";
import React, { Component } from 'react';
import axios, { CancelTokenSource } from 'axios';
import { Grid, Icon, Modal } from 'semantic-ui-react';
import Input from 'components/final-form/Input';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import { Field, Form as FinalForm, FormSpy } from 'react-final-form';
import { debounce } from 'ts-debounce';
import { FormState } from 'final-form';
import { MandateConfigDto, MandateDto } from 'ts-types/api.types';
import { deleteMandate, findMandateById, searchMandates } from 'services/mandateConfigServices';
import UpsertMandateForm from 'components/user-settings/UpsertMandateForm';
import { noop } from 'utils/functionUtils';
import LoaderComponent from 'components/LoaderComponent';
import DeleteActionButton from 'components/DeleteActionButton';
import { getAllMandates } from 'services/mandateServices';
import FormModalContentContainer from 'components/layout/FormModalContentContainer';
import PxGrid from 'components/layout/PxGrid';
import PraxStyledSegment from 'components/layout/PraxStyledSegment';
import { getDefaultBills } from 'services/mandateBillServices';
import EditActionButton from 'components/EditActionButton';
import AddButton from 'components/AddButton';
import StyledViewTableRow from 'components/layout/StyledViewTableRow';
import SearchResultComponent from 'components/SearchResultComponent';

interface Props extends MessageSourceProps {
}

interface State {
  foundMandates: Array<MandateDto>,
  mandatesLoaded: boolean,
  mandate?: Partial<MandateConfigDto>,
  mandateId?: number,
  addEditModalOpen: boolean,
  loading: boolean,
  searchValues: string,
  cancelTokenSource: CancelTokenSource,
}

interface MandateFieldProps extends FormState {
  mandateSearchTerm?: String
}

class MandatesView extends Component<Props, State> {

  private mounted: boolean = false;

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    this.state = {
      foundMandates: [],
      mandatesLoaded: false,
      mandate: undefined,
      mandateId: undefined,
      addEditModalOpen: false,
      searchValues: "",
      loading: false,
      cancelTokenSource
    }
  }

  componentDidMount(): void {
    this.mounted = true;
  }

  componentWillUnmount(): void {
    const {cancelTokenSource} = this.state;

    this.mounted = false;
    cancelTokenSource.cancel('Operation canceled by user');
  }

  openAddEditModal = (id?: number) => {
    const {cancelTokenSource} = this.state;
    if (id) {
      findMandateById(id, cancelTokenSource)
          .then(response => this.showModal(response, id))
          .catch(noop);
    } else {
      getDefaultBills(cancelTokenSource)
          .then((response => {
            let mandate: Partial<MandateConfigDto> = {bills: response};
            this.showModal(mandate);
          }))
          .catch(noop)

    }
  };

  initialMandateValues = (): Partial<MandateConfigDto> => {

    return {
      active: true,
      praxis: 0,
      banks: [],
      mandateSections: []
    };
  };

  showModal = (mandate: Partial<MandateConfigDto>, id?: number) => {
    if (!id) {
      mandate = {...mandate, ...this.initialMandateValues()};
    }
    this.setState(({}: State) => {
      return {
        addEditModalOpen: true,
        mandate: mandate,
        mandateId: id
      }
    });
  };

  hideModal = () => {
    this.setState({
      addEditModalOpen: false,
      mandateId: undefined,
      mandate: undefined
    });
  };

  onSaveUpsertForm = () => {
    const {searchValues, cancelTokenSource} = this.state;

    const onFinally = () => {
      this.setState({
        mandatesLoaded: true
      })
    };

    searchMandates(searchValues, cancelTokenSource)
        .then(response => {
          this.setState({
            foundMandates: response
          });
        })
        .catch(noop)
        .finally(onFinally);

    this.hideModal();
  };

  deleteMandate = (id: number): void => {
    const {foundMandates, cancelTokenSource} = this.state;

    deleteMandate(id, cancelTokenSource)
        .then(response => {
          this.setState({
            foundMandates: foundMandates.filter(mandate => mandate.id != response.id)
          })
        }).catch(noop);
  };

  handleChange = debounce(({values}: MandateFieldProps): void => {
    if (!this.mounted) {
      return;
    }

    const {mandateSearchTerm} = values;
    const {cancelTokenSource} = this.state;

    const onSuccess = (response: Array<MandateDto>) => {
      this.setState({
        foundMandates: response
      });
    };

    const onFinally = () => {
      this.setState({
        mandatesLoaded: true
      })
    };

    this.setState({
      mandatesLoaded: false,
      searchValues: values.mandateSearchTerm ? values.mandateSearchTerm : ""
    });

    if (mandateSearchTerm) {
      searchMandates(mandateSearchTerm, cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    } else {
      getAllMandates(cancelTokenSource)
          .then(onSuccess)
          .catch(noop)
          .finally(onFinally);
    }
  }, 300);

  render(): React.ReactNode {
    const {getMessage} = this.props;
    const {mandatesLoaded} = this.state;

    return (
        <PraxStyledSegment>

          <div>
            {this.renderSearchMandatesSegment()}
          </div>

          <StyledViewTableRow>
            {!mandatesLoaded ? <LoaderComponent message={getMessage("mandate.loading")}/> : this.renderMandatesegment()}
          </StyledViewTableRow>

          {this.renderUpsertMandateModal()}
        </PraxStyledSegment>
    );
  }

  renderSearchMandatesSegment = () => {
    return (
        <FinalForm
            onSubmit={() => {
            }}
            subscription={{pristine: true}}
            render={this.renderSearchForm}
        />
    );
  };

  renderSearchForm = (): React.ReactNode => {
    const {getMessage} = this.props;
    const {foundMandates} = this.state;

    const numResults = foundMandates.length;

    return (
        <PxGrid>
          <Grid.Row>
            <Grid.Column width={3} verticalAlign="middle">
              {getMessage("select")}:
            </Grid.Column>
            <Grid.Column width={13}>
              <Field
                  name="mandateSearchTerm"
                  component={Input}
                  fluid
                  autoFocus
              />
            </Grid.Column>
          </Grid.Row>
          <Grid.Row>
            <Grid.Column>
              <AddButton buttonMessage={getMessage("button.add")} onClick={() => this.openAddEditModal()}/>
            </Grid.Column>
          </Grid.Row>
          <SearchResultComponent numResults={numResults} textMessage={"search.resultsSummary"}/>
          <FormSpy subscription={{values: true}} onChange={this.handleChange}/>
        </PxGrid>
    );
  };

  renderMandatesegment = (): JSX.Element => {
    const {getMessage} = this.props;
    const {foundMandates} = this.state;

    return (
        <VirtualizedTable
            rowCount={foundMandates.length}
            rowGetter={this.mandateRowGetter}
            rowRenderer={this.foundMandatesRowRenderer}
            rowHeight={42}
            columns={[
              {
                width: 100,
                label: "#",
                dataKey: "index"
              },
              {
                width: 250,
                flexGrow: 1,
                label: (getMessage("mandate.name")),
                dataKey: "name",
                cellRenderer: this.mandateNameCellRenderer
              },
              {
                width: 250,
                flexGrow: 1,
                label: (getMessage("mandate.street")),
                dataKey: "street"
              },
              {
                width: 150,
                label: (getMessage("mandate.zip")),
                dataKey: "zip"
              },
              {
                width: 150,
                label: (getMessage("mandate.city")),
                dataKey: "city"
              },
              {
                width: 500,
                flexGrow: 1,
                label: (getMessage("mandate.speciality")),
                dataKey: "speciality"
              },
              {
                width: 200,
                label: (getMessage("mandate.eanNumber")),
                dataKey: "eanNumber"
              },
              {
                width: 200,
                label: (getMessage("mandate.zsrNumber")),
                dataKey: "zsrNumber"
              },
              {
                width: 100,
                label: (getMessage("actions")),
                dataKey: "id",
                cellRenderer: (this.actionsCellRenderer)
              }
            ]}
        />
    );
  };

  mandateRowGetter = ({index}: any) => {
    const {foundMandates} = this.state;

    Object.assign(foundMandates[index], {index: index + 1});

    return foundMandates[index]
  };

  foundMandatesRowRenderer = ({className, columns, index, key, style}: any) => {
    const a11yProps = {'aria-rowindex': index + 1};
    const {foundMandates} = this.state;

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={style}
            onDoubleClick={() => this.openAddEditModal(foundMandates[index].id)}
        >
          {columns}
        </div>
    );
  };

  actionsCellRenderer = ({cellData}: any) => {
    const {getMessage} = this.props;

    return (
        <React.Fragment>
          <EditActionButton
              hoverMessage={getMessage('msgEdit')}
              onConfirm={() => this.openAddEditModal(cellData)}
          />
          <DeleteActionButton
              hoverMessage={getMessage('msgDelete')}
              popupMessage={getMessage("mandate.confirmDelete")}
              onConfirm={() => this.deleteMandate(cellData)}
          />
        </React.Fragment>
    );
  };

  mandateNameCellRenderer = ({rowData, cellData}: any) => {
    const mandateColor = rowData.color;
    const mandateName = cellData ? cellData : "";

    return (
        <div>
          {mandateColor &&
              <div style={{display: "inline-flex"}}>
                <Icon
                    name="circle"
                    style={{color: `${mandateColor}`}}
                    size='small'
                    title={mandateName}
                />
              </div>
          }
          <span>{mandateName}</span>
        </div>
    )
  }

  renderUpsertMandateModal = (): React.ReactNode => {

    const {getMessage} = this.props;
    const {addEditModalOpen, mandate, mandateId} = this.state;

    return (
        <Modal
            open={addEditModalOpen}
            onClose={this.hideModal}
            closeOnDimmerClick={false}
        >
          <Modal.Header>{getMessage("mandate")}</Modal.Header>
          <FormModalContentContainer>
            <UpsertMandateForm
                mandate={mandate}
                mandateId={mandateId}
                onSave={this.onSaveUpsertForm}
                onCancel={this.hideModal}
            />
          </FormModalContentContainer>
        </Modal>
    )
  }

}

export default withMessages(MandatesView);
